import { c as _c } from "react/compiler-runtime";
import { useCompatRouter } from "@website/hooks/compat-router";
import { useIsVisible } from "@website/hooks/is-visible";
import { useViewPort } from "@website/hooks/viewport";
import { Button } from "@website/ui/button";
import { MiniChips } from "@website/ui/mini-chips";
import { Modal } from "@website/ui/modal";
import omit from "lodash-es/omit";
import { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { AccommodationCardClickArea, AccommodationCardPropertiesType } from "./accommodation-card.types";
import { AccommodationCardAddress } from "./components/accommodation-card-address";
import { AccommodationCardBadges } from "./components/accommodation-card-badges";
import { AccommodationCardCapacity } from "./components/accommodation-card-capacity";
import { AccommodationCardLink } from "./components/accommodation-card-link/accommodation-card-link";
import { AccommodationCardMedia } from "./components/accommodation-card-media/accommodation-card-media";
import { AccommodationCardPricing } from "./components/accommodation-card-pricing/accommodation-card-pricing";
import { AccommodationCardRating } from "./components/accommodation-card-rating";
import { AccommodationCardReservationInfo } from "./components/accommodation-card-reservation-info/accommodation-card-reservation-info";
import { AccommodationCardTitle } from "./components/accommodation-card-title";
const getAccommodationCardClickArea = (event: MouseEvent<HTMLElement>): AccommodationCardClickArea | undefined => {
  const target = event.target as Element | null;
  if (!target?.closest) return undefined;
  if (target.closest(".accommodation-card-image")) return "image_click";
  if (target.closest(".accommodation-card-title")) return "title_click";
  if (target.closest(".accommodation-card-pricing")) return "price_click";
  return undefined;
};
export const AccommodationCard: FC<AccommodationCardPropertiesType> = t0 => {
  const $ = _c(73);
  let cardProperties;
  let handleTrackingEvents;
  if ($[0] !== t0) {
    ({
      handleTrackingEvents,
      ...cardProperties
    } = t0);
    $[0] = t0;
    $[1] = cardProperties;
    $[2] = handleTrackingEvents;
  } else {
    cardProperties = $[1];
    handleTrackingEvents = $[2];
  }
  const {
    advertisementBadge,
    capacity,
    media,
    address,
    id,
    title,
    rating,
    pricing,
    reservationInfo,
    link,
    badges,
    noFollow
  } = cardProperties;
  const router = useCompatRouter();
  const [clickLoading, setClickLoading] = useState(false);
  const {
    isDesktop
  } = useViewPort();
  const {
    reference,
    inView
  } = useIsVisible();
  const isViewTrackedReference = useRef(false);
  const partnershipBadge = media.badges?.partnership;
  const badgeInfoModal = `badge-info-${id}`;
  const isBadgeInfoModalOpen = router?.query?.modal === badgeInfoModal && !isDesktop && Boolean(partnershipBadge?.text);
  let t1;
  if ($[3] !== router) {
    t1 = () => router.replace(router?.pathname, omit(router?.query, "modal"), {
      shallow: true,
      scroll: false
    });
    $[3] = router;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const onCloseBadgeInfo = t1;
  let t2;
  if ($[5] !== cardProperties || $[6] !== handleTrackingEvents || $[7] !== link) {
    t2 = event => {
      setClickLoading(true);
      link.onClick?.(event);
      handleTrackingEvents?.({
        ...cardProperties,
        clickArea: getAccommodationCardClickArea(event),
        trackingEvent: "click"
      });
    };
    $[5] = cardProperties;
    $[6] = handleTrackingEvents;
    $[7] = link;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleCardClick = t2;
  let t3;
  if ($[9] !== cardProperties || $[10] !== handleTrackingEvents) {
    t3 = event_0 => {
      handleTrackingEvents?.({
        ...cardProperties,
        clickArea: getAccommodationCardClickArea(event_0),
        trackingEvent: "click"
      });
    };
    $[9] = cardProperties;
    $[10] = handleTrackingEvents;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleDetailClick = t3;
  let t4;
  let t5;
  if ($[12] !== cardProperties || $[13] !== handleTrackingEvents || $[14] !== inView) {
    t4 = () => {
      if (!inView || !handleTrackingEvents || isViewTrackedReference.current) {
        return;
      }
      isViewTrackedReference.current = true;
      handleTrackingEvents({
        ...cardProperties,
        trackingEvent: "view"
      });
    };
    t5 = [cardProperties, handleTrackingEvents, inView];
    $[12] = cardProperties;
    $[13] = handleTrackingEvents;
    $[14] = inView;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  useEffect(t4, t5);
  let t6;
  if ($[17] !== clickLoading || $[18] !== handleDetailClick || $[19] !== id || $[20] !== link || $[21] !== media) {
    t6 = <AccommodationCardMedia {...media} detailLink={link} id={id} clickLoading={clickLoading} setClickLoading={setClickLoading} onDetailClick={handleDetailClick} />;
    $[17] = clickLoading;
    $[18] = handleDetailClick;
    $[19] = id;
    $[20] = link;
    $[21] = media;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== advertisementBadge) {
    t7 = advertisementBadge && <MiniChips color="ads" size="small" text={advertisementBadge?.title} variant="contained" />;
    $[23] = advertisementBadge;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== title) {
    t8 = <AccommodationCardTitle title={title} />;
    $[25] = title;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = <div className="flex items-center gap-1">{t7}{t8}</div>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== address) {
    t10 = <AccommodationCardAddress address={address} />;
    $[30] = address;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== rating) {
    t11 = <AccommodationCardRating {...rating} />;
    $[32] = rating;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11) {
    t12 = <div className="mt-1 flex items-center justify-between">{t10}{t11}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== capacity) {
    t13 = <AccommodationCardCapacity {...capacity} />;
    $[37] = capacity;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== reservationInfo) {
    t14 = <AccommodationCardReservationInfo {...reservationInfo} />;
    $[39] = reservationInfo;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const t15 = reservationInfo?.guestsCount > 0 && reservationInfo?.nightCount > 0;
  let t16;
  if ($[41] !== pricing || $[42] !== t15) {
    t16 = <AccommodationCardPricing {...pricing} showTotalPrice={t15} />;
    $[41] = pricing;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t14 || $[45] !== t16) {
    t17 = <div className="mt-1.5 flex items-center justify-between">{t14}{t16}</div>;
    $[44] = t14;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const t18 = badges?.list;
  const t19 = badges?.snappProDiscountPercent;
  let t20;
  if ($[47] !== t18 || $[48] !== t19) {
    t20 = <AccommodationCardBadges list={t18} snappProDiscountPercent={t19} />;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t12 || $[51] !== t13 || $[52] !== t17 || $[53] !== t20 || $[54] !== t6 || $[55] !== t9) {
    t21 = <div className="flex flex-col">{t6}{t9}{t12}{t13}{t17}{t20}</div>;
    $[50] = t12;
    $[51] = t13;
    $[52] = t17;
    $[53] = t20;
    $[54] = t6;
    $[55] = t9;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== handleCardClick || $[58] !== link || $[59] !== noFollow || $[60] !== t21) {
    t22 = <AccommodationCardLink {...link} noFollow={noFollow} onClick={handleCardClick}>{t21}</AccommodationCardLink>;
    $[57] = handleCardClick;
    $[58] = link;
    $[59] = noFollow;
    $[60] = t21;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== reference || $[63] !== t22) {
    t23 = <div className="h-full" ref={reference}>{t22}</div>;
    $[62] = reference;
    $[63] = t22;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== isBadgeInfoModalOpen || $[66] !== onCloseBadgeInfo || $[67] !== partnershipBadge?.description || $[68] !== partnershipBadge?.text) {
    t24 = isBadgeInfoModalOpen && <div className="block md:hidden"><Modal animate="bottom" onClose={onCloseBadgeInfo} open={isBadgeInfoModalOpen} mobileHeight="auto" title={partnershipBadge?.text} usePortal={true}><div className="flex w-full flex-col gap-4 px-6 pb-0"><span className="text-body-2">{partnershipBadge?.description}</span><Button color="secondary" onClick={onCloseBadgeInfo} text={"\u0645\u062A\u0648\u062C\u0647 \u0634\u062F\u0645"} variant="outline" /></div></Modal></div>;
    $[65] = isBadgeInfoModalOpen;
    $[66] = onCloseBadgeInfo;
    $[67] = partnershipBadge?.description;
    $[68] = partnershipBadge?.text;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== t23 || $[71] !== t24) {
    t25 = <>{t23}{t24}</>;
    $[70] = t23;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  return t25;
};